// ============================================================================
// Section error boundary — contain a render crash to ONE section of the order page.
//
// Why this exists: the OMS detail page renders values the backend derives from
// Zoho / Loginext responses. When one of those arrived as an object instead of a
// string (a Loginext reject envelope in a checklist row's `error`), React threw
// during render — and with no boundary anywhere above it, the whole order page
// unmounted to a blank white screen. The one order an operator most needed to
// look at was the one they could not open.
//
// A page that loses its Escalations panel but still shows the lines, allocations
// and legs is strictly better than a page that shows nothing. So each independent
// section of the detail view is wrapped in one of these: the failure is reported
// in place, the rest of the page survives, and the console still gets the real
// error for whoever debugs it.
// ============================================================================
import { WarningOutlined } from "@ant-design/icons";
import { Alert } from "antd";
import React from "react";

type Props = {
  /** Section name, used in the fallback copy: "Escalations could not be displayed". */
  name: string;
  children: React.ReactNode;
};

type State = { error: Error | null };

class SectionBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the real stack in the console — the fallback below is deliberately
    // short, and this is what a developer needs when someone reports the banner.
    // eslint-disable-next-line no-console
    console.error(`[oms] ${this.props.name} section failed to render`, error, info);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <Alert
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        message={`${this.props.name} could not be displayed`}
        description={
          <span style={{ fontSize: 12 }}>
            The rest of this order is unaffected. Details are in the browser console
            {error.message ? `: ${error.message}` : "."}
          </span>
        }
        style={{ marginBottom: 12 }}
      />
    );
  }
}

export default SectionBoundary;
